import type { ProvaRuoliLinkDto, TimoneDto, TimoneNodeDto, TimoneRoleDto, TimoneSection } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { useSinapsiFonts } from './sinapsi-fonts';
import { SinapsiNet, type NetOptions } from './timone-sinapsi';
import './timone.css';
import '../../prova/prova.css';

/**
 * La pagina «Al timone» (contratto n. 1, fase 13; ADR 0162), nello stile «Sinapsi»: il server compone mappa, pianta
 * viva, almeno 5 dashboard di ruolo, prezzo del listino, rotta e promessa; il cervello sceglie da cosa partire, l'ordine
 * delle sezioni e le parole. Finisce con «Acquista», che registra la richiesta: il pagamento viene dopo.
 */

const KEY = ['onboarding', 'timone'];
const eur = (n: number) => `${Math.round(n).toLocaleString('it-IT')} €`;
const h = (n: number) => `${n.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ore`;
const LIVELLO: Record<TimoneRoleDto['livello'], string> = {
  direzione: 'direzione',
  responsabile: 'responsabile',
  operativo: 'operativo',
};

function useNet(ref: React.RefObject<HTMLCanvasElement | null>, o: NetOptions) {
  const net = useRef<SinapsiNet | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    net.current = new SinapsiNet(ref.current, o);
    return () => net.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    net.current?.update(o);
  });
}

function clustersOf(d: TimoneDto) {
  return d.pianta.sedi
    .flatMap((s) =>
      s.reparti.map((r) => ({
        nome: d.pianta.sedi.length > 1 ? `${r.nome} (${s.nome})` : r.nome,
        persone: r.persone,
        ore_mese: r.ore_mese,
      })),
    )
    .slice(0, 9);
}

export function AlTimone() {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  useSinapsiFonts();
  const q = useTenantQuery<TimoneDto>(KEY, '/onboarding/timone', {
    refetchInterval: (d) => (d?.in_preparazione ? 5000 : false),
  });
  const buy = useMutation({
    mutationFn: () => api<TimoneDto>(t('/onboarding/timone/acquista'), { method: 'POST' }),
    onSuccess: (x) => qc.setQueryData(['tenant', tenant.id, ...KEY], x),
  });
  const hero = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLElement>(null);
  const [dock, setDock] = useState(false);
  useEffect(() => {
    const seen = new Map<Element, boolean>();
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => seen.set(e.target, e.isIntersecting));
      const heroGone = hero.current ? hero.current.getBoundingClientRect().bottom < 0 : false;
      setDock(heroGone && !(close.current && seen.get(close.current)));
    });
    if (hero.current) io.observe(hero.current);
    if (close.current) io.observe(close.current);
    return () => io.disconnect();
  }, [q.data?.azienda.nome]);

  const d = q.data;
  if (q.error) return <p className="text-sm text-red-700">{errorMessage(q.error)}</p>;
  if (!d) return null;
  const p = d.prezzo;
  const onBuy = () => !d.acquisto && buy.mutate();
  const sections: Record<TimoneSection, ReactNode> = {
    mappa: <Mappa d={d} />,
    pianta: <Pianta d={d} />,
    ruoli: <Ruoli d={d} />,
    calcolo: <Calcolo d={d} />,
    rotta: <Rotta d={d} />,
    promessa: <Promessa d={d} />,
  };
  return (
    <div className="tm" id="al-timone" aria-label="Al timone">
      <Hero d={d} heroRef={hero} onBuy={onBuy} pending={buy.isPending} />
      {d.testi.ordine.map((k) => (
        <section key={k} className="tm-sec" id={`tm-${k}`}>
          <div className="tm-head">
            <h3>{d.testi.sezioni[k].titolo}</h3>
            <p>{d.testi.sezioni[k].testo}</p>
          </div>
          {sections[k]}
        </section>
      ))}
      <section className="tm-close" ref={close}>
        <div>
          <span className="tm-ey">Il timone di {d.azienda.nome}</span>
          <h3 style={{ marginTop: 10 }}>{d.testi.chiusura.titolo}</h3>
          <p>{d.testi.chiusura.testo}</p>
          <div style={{ marginTop: 22 }}>
            <BuyButton d={d} onBuy={onBuy} pending={buy.isPending} />
          </div>
          {buy.error && (
            <p className="tm-note" style={{ color: '#ff9b9b', marginTop: 10 }}>
              {errorMessage(buy.error)}
            </p>
          )}
          {d.acquisto && <Done d={d} />}
        </div>
        <div className="tm-promise">
          <div>
            <b>{p.prova_giorni} giorni gratis</b>Provi Timonio con tutta l’azienda prima di pagare.
          </div>
          <div>
            <b>Il prezzo segue il valore</b>
            {p.prezzo_mese !== null && p.valore_mese !== null
              ? `${eur(p.prezzo_mese)} al mese: il ${Math.round(p.quota * 100)}% dei ${eur(p.valore_mese)} di tempo che Timonio fa risparmiare.`
              : p.perche_senza_prezzo}
          </div>
          <div>
            <b>Promesso accanto al misurato</b>Ogni mese, per ogni obiettivo. Se la rotta devia, te
            lo diciamo noi.
          </div>
        </div>
      </section>
      <p className="tm-foot">
        Dashboard, persone e volumi sono esempi costruiti sui dati della tua azienda, mai dati veri
        di persone. Ore ed euro sono valore del tempo, non cassa: diventano risparmio quando evitano
        ore pagate. Timonio propone un assetto ideale con le fonti; non sostituisce il consulente
        del lavoro, l’RSPP, il commercialista o l’avvocato.{' '}
        {d.testi.fonte === 'cervello'
          ? 'Testi scritti dal consulente di Timonio per la tua azienda.'
          : ''}
      </p>
      <div className={`tm-dock${dock && !d.acquisto ? ' show' : ''}`} aria-hidden={!dock}>
        <div className="tm-dock-in">
          <div>
            <b>{p.prezzo_mese !== null ? `Timonio · ${eur(p.prezzo_mese)}/mese` : 'Timonio'}</b>{' '}
            <span>
              · {p.prova_giorni} giorni gratis
              {p.valore_mese ? ` · libera ${eur(p.valore_mese)} di tempo al mese` : ''}
            </span>
          </div>
          <button
            type="button"
            className="tm-buy"
            tabIndex={dock ? 0 : -1}
            onClick={onBuy}
            disabled={buy.isPending}
          >
            ⎈ Acquista
          </button>
        </div>
      </div>
    </div>
  );
}

function BuyButton({ d, onBuy, pending }: { d: TimoneDto; onBuy: () => void; pending: boolean }) {
  if (d.acquisto) return <span className="tm-trial">✓ Richiesta registrata</span>;
  return (
    <button type="button" className="tm-buy" onClick={onBuy} disabled={pending}>
      ⎈{' '}
      {pending
        ? 'Registro la richiesta…'
        : `Acquista Timonio${d.prezzo.prezzo_mese !== null ? ` · ${eur(d.prezzo.prezzo_mese)}/mese` : ''}`}
    </button>
  );
}

function Done({ d }: { d: TimoneDto }) {
  const a = d.acquisto!;
  const quando = new Date(a.richiesto_il).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <div className="tm-done" role="status">
      <b>Benvenuto a bordo. La tua richiesta è registrata.</b>
      <p>
        Richiesta del {quando}
        {a.prezzo_mese !== null ? ` per ${eur(a.prezzo_mese)} al mese` : ''}, con i primi{' '}
        {a.prova_giorni} giorni gratuiti. Il pagamento si attiva in un secondo momento: prima di
        allora non ti viene addebitato nulla.
      </p>
    </div>
  );
}

function Hero({
  d,
  heroRef,
  onBuy,
  pending,
}: {
  d: TimoneDto;
  heroRef: React.RefObject<HTMLDivElement | null>;
  onBuy: () => void;
  pending: boolean;
}) {
  const cv = useRef<HTMLCanvasElement>(null);
  const clusters = useMemo(() => clustersOf(d), [d]);
  const wide = typeof window !== 'undefined' && window.innerWidth > 860;
  useNet(cv, {
    clusters,
    sorgenti: d.mappa.fonti.map((f) => f.nome),
    centro: 'Timonio',
    modo: 'timone',
    apertura: 1,
    fuoco: wide ? [0.73, 0.52] : [0.5, 0.52],
    etichette: wide,
  });
  const p = d.prezzo;
  const persone = d.pianta.sedi.reduce(
    (a, s) => a + s.reparti.reduce((b, r) => b + (r.persone ?? 0), 0),
    0,
  );
  return (
    <div className="tm-hero" ref={heroRef}>
      <canvas ref={cv} aria-hidden />
      <div className="tm-vign" />
      <div className="tm-hero-ui">
        <div>
          <span className="tm-ey">{d.testi.occhiello}</span>
          <h2>{d.testi.titolo}</h2>
          <p className="tm-sub">{d.testi.sottotitolo}</p>
          <div className="tm-proof">
            {d.mappa.fonti.length > 0 && (
              <span>
                Riconosciuta da{' '}
                {d.mappa.fonti
                  .slice(0, 3)
                  .map((f) => f.nome.toLowerCase())
                  .join(', ')}
              </span>
            )}
            {clusters.length > 0 && (
              <span>
                {clusters.length} reparti{persone ? ` · ${persone} persone` : ''}
              </span>
            )}
            {d.ruoli.length > 0 && <span>{d.ruoli.length} ruoli al lavoro con Timonio</span>}
          </div>
          <aside className="tm-price" aria-label="L’abbonamento">
            <div className="tm-plan">
              <b>Timonio per {d.azienda.nome}</b>
              <span>mensile</span>
            </div>
            {p.prezzo_mese !== null ? (
              <div className="tm-amt">
                {eur(p.prezzo_mese)} <small>al mese</small>
              </div>
            ) : (
              <p className="tm-note">{p.perche_senza_prezzo}</p>
            )}
            <span className="tm-trial">I primi {p.prova_giorni} giorni sono gratis</span>
            {p.valore_mese !== null && p.prezzo_mese !== null && (
              <dl className="tm-vs">
                <div>
                  <dt>Costa</dt>
                  <dd>{eur(p.prezzo_mese)}</dd>
                </div>
                <div className="tm-gain">
                  <dt>Libera, in valore</dt>
                  <dd>{eur(p.valore_mese)}</dd>
                </div>
              </dl>
            )}
            {p.giorni_per_ripagare !== null && (
              <p className="tm-note">
                Si ripaga in {p.giorni_per_ripagare} giorni lavorativi di tempo liberato.
              </p>
            )}
            {d.acquisto ? (
              <span className="tm-trial">✓ Richiesta registrata</span>
            ) : (
              <BuyButton d={d} onBuy={onBuy} pending={pending} />
            )}
            <details className="tm-note">
              <summary style={{ cursor: 'pointer' }}>Come si calcola il prezzo</summary>
              {p.formula}.
            </details>
          </aside>
        </div>
      </div>
    </div>
  );
}

/** Un generatore ripetibile, per disegnare sempre lo stesso grappolo di neuroni al centro della mappa. */
function seeded(seed: number) {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
}

function Mappa({ d }: { d: TimoneDto }) {
  const [sel, setSel] = useState<TimoneNodeDto | null>(null);
  const CX = 600;
  const CY = 400;
  const brain = useMemo(() => {
    const r = seeded(42);
    const pts = Array.from({ length: 70 }, () => {
      const a = r() * Math.PI * 2;
      const k = Math.sqrt(r());
      return [CX + Math.cos(a) * 150 * k, CY + Math.sin(a) * 105 * k] as const;
    });
    const lines: Array<[number, number]> = [];
    pts.forEach((p, i) => {
      pts
        .map((q, j) => [j, (q[0] - p[0]) ** 2 + (q[1] - p[1]) ** 2] as const)
        .filter(([j]) => j > i)
        .sort((a, b) => a[1] - b[1])
        .slice(0, 2)
        .forEach(([j]) => lines.push([i, j]));
    });
    return { pts, lines };
  }, []);
  type Placed = {
    n: TimoneNodeDto;
    x: number;
    y: number;
    anchor: 'middle' | 'start' | 'end';
    ty: number;
    tx: number;
    col: string;
  };
  const placed: Placed[] = [];
  const row = (xs: TimoneNodeDto[], y: number, gap: number, col: string, below: boolean) =>
    xs.forEach((n, i) =>
      placed.push({
        n,
        x: CX + (i - (xs.length - 1) / 2) * gap,
        y,
        anchor: 'middle',
        tx: 0,
        ty: below ? 30 : -22,
        col,
      }),
    );
  row(d.mappa.fonti, 90, 190, '#ffffff', false);
  d.mappa.programmi.forEach((n, i) =>
    placed.push({ n, x: 60, y: 210 + i * 72, anchor: 'start', tx: 20, ty: 4, col: '#3cf0ff' }),
  );
  d.mappa.quotidiani.forEach((n, i) =>
    placed.push({ n, x: 1140, y: 210 + i * 72, anchor: 'end', tx: -20, ty: 4, col: '#c9b5ff' }),
  );
  row(d.mappa.restituisce, 690, 260, '#ffd68c', true);
  const shown = sel ?? {
    nome: d.azienda.nome,
    sotto: d.azienda.luogo,
    grado: null,
    fonte: d.azienda.in_breve ?? 'la tua azienda progettata nelle fasi della configurazione',
  };
  return (
    <div>
      <div className="tm-scroll">
        <svg
          viewBox="0 0 1200 780"
          role="img"
          aria-label={`Mappa di Timonio per ${d.azienda.nome}`}
        >
          <defs>
            <radialGradient id="tm-halo">
              <stop offset="0" stopColor="#3cf0ff" stopOpacity="0.28" />
              <stop offset="1" stopColor="#3cf0ff" stopOpacity="0" />
            </radialGradient>
            <filter id="tm-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <text
            x={CX}
            y={36}
            textAnchor="middle"
            fill="#8b97b5"
            style={{ font: '500 11px var(--tm-mono)', letterSpacing: '.14em' }}
          >
            I DATI DI {d.azienda.nome.toUpperCase()}
          </text>
          {placed.map((p, i) => (
            <path
              key={`s${i}`}
              className="tm-syn"
              stroke={p.col}
              strokeOpacity={0.55}
              d={`M${p.x},${p.y} C${p.x + (CX - p.x) * 0.5},${p.y} ${CX + (p.x - CX) * 0.2},${CY + (p.y - CY) * 0.4} ${CX + (p.x - CX) * 0.12},${CY + (p.y - CY) * 0.12}`}
            />
          ))}
          <circle cx={CX} cy={CY} r={240} fill="url(#tm-halo)" />
          <g filter="url(#tm-glow)">
            {brain.lines.map(([a, b], i) => (
              <line
                key={i}
                x1={brain.pts[a]![0]}
                y1={brain.pts[a]![1]}
                x2={brain.pts[b]![0]}
                y2={brain.pts[b]![1]}
                stroke="#8a5cff"
                strokeOpacity={0.45}
                strokeWidth={0.8}
              />
            ))}
            {brain.pts.map(([x, y], i) => (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={i % 9 === 0 ? 3 : 1.6}
                fill={i % 9 === 0 ? '#3cf0ff' : '#c9b5ff'}
              />
            ))}
          </g>
          <g transform={`translate(${CX},${CY})`} filter="url(#tm-glow)">
            <circle r={26} fill="none" stroke="#ffd68c" strokeWidth={2.5} />
            {Array.from({ length: 8 }, (_, k) => (
              <line
                key={k}
                x1={Math.cos((k * Math.PI) / 4) * 7}
                y1={Math.sin((k * Math.PI) / 4) * 7}
                x2={Math.cos((k * Math.PI) / 4) * 38}
                y2={Math.sin((k * Math.PI) / 4) * 38}
                stroke="#ffd68c"
                strokeWidth={2.5}
                strokeLinecap="round"
              />
            ))}
          </g>
          <text
            x={CX}
            y={CY + 150}
            textAnchor="middle"
            fill="#eaf0ff"
            style={{ font: '700 20px var(--tm-disp)' }}
          >
            {d.azienda.nome}
          </text>
          <text
            x={CX}
            y={CY + 170}
            textAnchor="middle"
            fill="#ffd68c"
            style={{ font: '600 11px var(--tm-mono)', letterSpacing: '.14em' }}
          >
            CON TIMONIO AL TIMONE
          </text>
          {placed.map((p, i) => (
            <g
              key={`n${i}`}
              className={`tm-map-node${sel === p.n ? ' on' : ''}`}
              tabIndex={0}
              role="button"
              aria-label={p.n.nome}
              onClick={() => setSel(p.n)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setSel(p.n))
              }
            >
              <circle cx={p.x} cy={p.y} r={16} fill={p.col} fillOpacity={0.12} />
              <circle className="tm-n" cx={p.x} cy={p.y} r={8} />
              <text x={p.x + p.tx} y={p.y + p.ty} textAnchor={p.anchor}>
                {p.n.nome.length > 34 ? `${p.n.nome.slice(0, 33)}…` : p.n.nome}
              </text>
              {p.n.sotto && (
                <text className="tm-s" x={p.x + p.tx} y={p.y + p.ty + 15} textAnchor={p.anchor}>
                  {p.n.sotto}
                </text>
              )}
            </g>
          ))}
          <text
            x={CX}
            y={760}
            textAnchor="middle"
            fill="#8b97b5"
            style={{ font: '500 11px var(--tm-mono)', letterSpacing: '.14em' }}
          >
            COSA FA TIMONIO PER TE
          </text>
        </svg>
      </div>
      <div className="tm-detail" aria-live="polite">
        <div className="tm-g">{shown.grado ?? '⎈'}</div>
        <div>
          <h4>
            {shown.nome}
            {shown.sotto ? ` · ${shown.sotto}` : ''}
          </h4>
          <p>Da dove viene: {shown.fonte}</p>
        </div>
      </div>
      {d.mappa.esclusi.length > 0 && (
        <p className="tm-grey">Non compaiono: {d.mappa.esclusi.join('; ')}.</p>
      )}
    </div>
  );
}

const SCENE_MS = [5000, 8000, 10000];

function Pianta({ d }: { d: TimoneDto }) {
  const cv = useRef<HTMLCanvasElement>(null);
  const clusters = useMemo(() => clustersOf(d), [d]);
  const reduce =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [t, setT] = useState(reduce ? SCENE_MS[0]! + SCENE_MS[1]! + 100 : 0);
  const [play, setPlay] = useState(!reduce);
  const tot = SCENE_MS.reduce((a, b) => a + b, 0);
  useEffect(() => {
    if (!play) return;
    const id = window.setInterval(() => setT((x) => (x + 100) % tot), 100);
    return () => window.clearInterval(id);
  }, [play, tot]);
  const scene = t < SCENE_MS[0]! ? 0 : t < SCENE_MS[0]! + SCENE_MS[1]! ? 1 : 2;
  const start = scene === 0 ? 0 : scene === 1 ? SCENE_MS[0]! : SCENE_MS[0]! + SCENE_MS[1]!;
  const local = t - start;
  const apertura = scene === 0 ? Math.min(1, local / (SCENE_MS[0]! * 0.8)) : 1;
  useNet(cv, {
    clusters,
    sorgenti: [],
    centro: d.azienda.nome,
    modo: scene === 2 ? 'timone' : 'oggi',
    apertura,
    fuoco: typeof window !== 'undefined' && window.innerWidth > 860 ? [0.64, 0.55] : [0.5, 0.64],
  });
  const sedi = d.pianta.sedi;
  const persone = sedi.reduce((a, s) => a + s.reparti.reduce((b, r) => b + (r.persone ?? 0), 0), 0);
  const caps = [
    {
      ey: `${d.azienda.nome} · le sedi`,
      big: 'Un punto di luce. Poi la tua azienda.',
      sm: `${sedi.map((s) => [s.nome, s.luogo].filter(Boolean).join(', ')).join(' · ') || 'La sede'}${persone ? ` · ${persone} persone` : ''}.`,
    },
    {
      ey: 'Oggi · i reparti',
      big: 'Ogni reparto, un grappolo di neuroni.',
      sm: `${clusters.length} reparti e i flussi di oggi: ogni impulso è un dato che passa da una persona all’altra.`,
    },
    {
      ey: 'Con Timonio',
      big: 'Le stesse stanze. Il doppio degli impulsi.',
      sm: d.pianta.ore_mese
        ? `I dati passano da soli e le ore tornano alle persone: ${h(d.pianta.ore_mese)} al mese.`
        : 'I dati passano da soli e le ore tornano alle persone.',
    },
  ];
  const c = caps[scene]!;
  if (!clusters.length)
    return (
      <p className="tm-grey">
        La pianta si sta ancora disegnando: i reparti compaiono appena l’organizzazione è pronta.
      </p>
    );
  return (
    <div>
      <div className="tm-stage">
        <canvas ref={cv} aria-hidden />
        <div className="tm-cap">
          <span className="tm-ey">{c.ey}</span>
          <b>{c.big}</b>
          <span>{c.sm}</span>
        </div>
      </div>
      <div className="tm-controls">
        <button type="button" className="tm-play" onClick={() => setPlay((x) => !x)}>
          {play ? 'Pausa' : 'Guarda'}
        </button>
        <div className="tm-chips">
          {['Le sedi', 'Oggi', 'Con Timonio'].map((l, i) => {
            const s0 = SCENE_MS.slice(0, i).reduce((a, b) => a + b, 0);
            const w = i < scene ? 100 : i > scene ? 0 : (local / SCENE_MS[i]!) * 100;
            return (
              <button
                key={l}
                type="button"
                className={`tm-chip${i === scene ? ' on' : ''}`}
                onClick={() => setT(s0 + (i === 0 ? 0 : 50))}
              >
                <i>
                  <b style={{ width: `${w}%` }} />
                </i>
                <span>{l}</span>
              </button>
            );
          })}
        </div>
      </div>
      <p className="tm-grey">
        Persone e volumi simulati sui dati della tua azienda: nessun nome vero.
      </p>
    </div>
  );
}

function Ruoli({ d }: { d: TimoneDto }) {
  const [i, setI] = useState(0);
  const r = d.ruoli[Math.min(i, d.ruoli.length - 1)];
  if (!r)
    return (
      <p className="tm-grey">
        Le dashboard dei ruoli compaiono appena l’organigramma e le procedure sono pronti.
      </p>
    );
  const x = r.dashboard;
  return (
    <div>
      <div className="tm-tabs" role="tablist">
        {d.ruoli.map((y, k) => (
          <button
            key={y.dashboard.ruolo}
            type="button"
            role="tab"
            className="tm-tab"
            aria-selected={k === i}
            onClick={() => setI(k)}
          >
            {y.dashboard.ruolo}
            <small>{LIVELLO[y.livello]}</small>
          </button>
        ))}
      </div>
      <div className="tm-app" role="tabpanel">
        <div className="tm-app-bar">
          <span>
            <b>Buongiorno</b> · {x.ruolo} ({LIVELLO[r.livello]}){x.reparto ? ` · ${x.reparto}` : ''}
            {x.sede ? `, ${x.sede}` : ''}
          </span>
          <span className="tm-tag">esempio</span>
        </div>
        <div className="tm-app-body">
          <div className="tm-col">
            {x.oggi[0] && (
              <div className="tm-first">
                <small>La prima cosa da fare</small>
                <p>{x.oggi[0].testo}</p>
              </div>
            )}
            <h5>Cosa vede ogni mattina</h5>
            <ul className="tm-list">
              {r.vede.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
            {x.scadenze.length > 0 && (
              <>
                <h5>Scadenze</h5>
                <ul className="tm-list">
                  {x.scadenze.slice(0, 3).map((s) => (
                    <li key={s.testo}>
                      {s.testo}
                      {s.quando ? ` · ${s.quando}` : ''}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="tm-col">
            {x.benefici?.ore_mese ? (
              <div className="tm-kpi">
                <b>{h(x.benefici.ore_mese)}</b>
                <span>al mese che tornano a questo ruolo</span>
              </div>
            ) : null}
            <h5>Cosa fa grazie a Timonio</h5>
            <ul className="tm-list tm-do">
              {r.fa.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
            {x.in_arrivo.length > 0 && (
              <p className="tm-note">In arrivo: {x.in_arrivo.join('; ')}.</p>
            )}
          </div>
        </div>
      </div>
      <ProvaPanel d={d} />
    </div>
  );
}

/**
 * La prova dei ruoli (contratto n. 2, art. 6.8; ADR 0173): il titolare crea un link da girare alle sue persone; chi lo
 * apre sceglie il suo ruolo senza dire chi è. Qui il titolare vede il link, quanti ruoli l'hanno provato e le proposte.
 */
function ProvaPanel({ d }: { d: TimoneDto }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const [copiato, setCopiato] = useState(false);
  const aggiorna = (prova: ProvaRuoliLinkDto | null) =>
    qc.setQueryData<TimoneDto>(['tenant', tenant.id, ...KEY], (old) => (old ? { ...old, prova } : old));
  const crea = useMutation({
    mutationFn: () => api<ProvaRuoliLinkDto>(t('/onboarding/timone/prova'), { method: 'POST' }),
    onSuccess: aggiorna,
  });
  const revoca = useMutation({
    mutationFn: () => api<void>(t('/onboarding/timone/prova'), { method: 'DELETE' }),
    onSuccess: () => aggiorna(null),
  });
  const p = d.prova;
  const copia = async () => {
    if (!p) return;
    try {
      await navigator.clipboard.writeText(p.url);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    } catch {
      /* senza appunti si seleziona il campo */
    }
  };
  const quando = (iso: string) =>
    new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
  return (
    <div className="tm-prova" aria-label="La prova dei ruoli">
      <span className="tm-ey">Falla provare alle tue persone</span>
      <h4>Ogni persona prova la dashboard del suo ruolo, prima che tu decida</h4>
      <p className="tm-note">
        Giri un link; chi lo apre sceglie il suo ruolo e vede la sua giornata con Timonio e cosa cambia per le sue
        competenze. Nessun nome, nessuna email: Timonio conta solo i ruoli provati e ti porta le proposte, senza
        dire chi le ha fatte. Puoi rigenerare o revocare il link quando vuoi.
      </p>
      {!p ? (
        <div className="azioni">
          <button type="button" className="p" disabled={crea.isPending} onClick={() => crea.mutate()}>
            {crea.isPending ? 'Creo il link…' : 'Crea il link di prova'}
          </button>
        </div>
      ) : (
        <>
          <div className="url">
            <input readOnly value={p.url} onFocus={(e) => e.currentTarget.select()} aria-label="Il link di prova" />
            <div className="azioni">
              <button type="button" className="p" onClick={copia}>
                {copiato ? 'Copiato ✓' : 'Copia'}
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Prova la tua giornata con Timonio in ${d.azienda.nome}: ${p.url}`)}`}
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp
              </a>
              <button type="button" disabled={crea.isPending} onClick={() => crea.mutate()}>
                Rigenera
              </button>
              <button type="button" className="x" disabled={revoca.isPending} onClick={() => revoca.mutate()}>
                Revoca
              </button>
            </div>
          </div>
          <p className="tm-note">Creato il {quando(p.creato_il)}. Rigenerandolo, quello vecchio non apre più nulla.</p>
          {p.aperture.length > 0 ? (
            <div className="aperture" aria-label="Ruoli provati">
              {p.aperture.map((a) => (
                <span key={a.ruolo}>
                  {a.ruolo} · {a.volte === 1 ? '1 volta' : `${a.volte} volte`}
                </span>
              ))}
            </div>
          ) : (
            <p className="tm-note">Nessuno l’ha ancora aperto.</p>
          )}
          {p.proposte.length > 0 && (
            <>
              <span className="tm-ey">
                {p.proposte.length === 1 ? '1 proposta d’acquisto' : `${p.proposte.length} proposte d’acquisto`} dalle tue persone
              </span>
              <ul className="proposte">
                {p.proposte.slice(0, 12).map((x, i) => (
                  <li key={`${x.quando}-${i}`}>
                    <b>{x.ruolo}</b> · {quando(x.quando)}
                    {x.riga ? ` · «${x.riga}»` : ''}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      {(crea.error || revoca.error) && (
        <p className="tm-note" style={{ color: '#ff9b9b' }}>
          {errorMessage(crea.error ?? revoca.error)}
        </p>
      )}
    </div>
  );
}

function Calcolo({ d }: { d: TimoneDto }) {
  const p = d.prezzo;
  const [ore, setOre] = useState(Math.round(p.ore_mese ?? 40));
  const [costo, setCosto] = useState(Math.round(p.costo_orario ?? 25));
  const [fid, setFid] = useState(100);
  if (p.prezzo_mese === null) return <p className="tm-grey">{p.perche_senza_prezzo}</p>;
  const valore = ore * costo * (fid / 100);
  const max = Math.max(valore, p.prezzo_mese);
  const m = valore / p.prezzo_mese;
  return (
    <div className="tm-calc">
      <div className="tm-calc-in">
        <label htmlFor="tm-ore">
          <span>
            Ore al mese che Timonio libera <output>{ore} h</output>
          </span>
          <input
            id="tm-ore"
            type="range"
            min={1}
            max={Math.max(20, Math.round((p.ore_mese ?? 40) * 1.5))}
            value={ore}
            onChange={(e) => setOre(+e.target.value)}
          />
        </label>
        <label htmlFor="tm-costo">
          <span>
            Costo orario medio <output>{costo} €/h</output>
          </span>
          <input
            id="tm-costo"
            type="range"
            min={10}
            max={80}
            value={costo}
            onChange={(e) => setCosto(+e.target.value)}
          />
        </label>
        <label htmlFor="tm-fid">
          <span>
            Quanto ti fidi della nostra stima <output>{fid}%</output>
          </span>
          <input
            id="tm-fid"
            type="range"
            min={10}
            max={100}
            step={5}
            value={fid}
            onChange={(e) => setFid(+e.target.value)}
          />
        </label>
      </div>
      <div className="tm-calc-out" aria-live="polite">
        <div className="tm-bar">
          <span>Valore del tempo liberato: {eur(valore)} al mese</span>
          <i style={{ width: `${(valore / max) * 100}%` }} />
        </div>
        <div className="tm-bar cost">
          <span>
            Abbonamento: {eur(p.prezzo_mese)} al mese, dopo {p.prova_giorni} giorni gratis
          </span>
          <i style={{ width: `${(p.prezzo_mese / max) * 100}%` }} />
        </div>
        <div className="tm-x">
          {m >= 1 ? `×${m.toLocaleString('it-IT', { maximumFractionDigits: 1 })}` : 'Non conviene'}
        </div>
        <p className="tm-note">
          {m >= 1
            ? `Ogni euro di abbonamento torna ${m.toLocaleString('it-IT', { maximumFractionDigits: 1 })} volte in tempo liberato.`
            : 'Con questi numeri l’abbonamento non si ripaga: te lo diciamo, e la rotta va rivista prima di partire.'}
        </p>
      </div>
    </div>
  );
}

function Rotta({ d }: { d: TimoneDto }) {
  if (!d.rotta.length)
    return (
      <p className="tm-grey">
        La rotta si compone appena le proposte reparto per reparto sono pronte.
      </p>
    );
  return (
    <div className="tm-route">
      {d.rotta.map((g) => (
        <article key={g.id} className="tm-leg">
          <span className="tm-day">
            Entro {g.scadenza_giorni} giorni · {g.reparto}
          </span>
          <h4>{g.titolo}</h4>
          <span className="tm-gainline">{g.traguardo}</span>
          <dl>
            <dt>Oggi</dt>
            <dd>{g.partenza}</dd>
            <dt>Fa Timonio</dt>
            <dd>{g.fa_timonio}</dd>
            {g.fa_azienda && (
              <>
                <dt>Fa l’azienda</dt>
                <dd>{g.fa_azienda}</dd>
              </>
            )}
            <dt>Si misura</dt>
            <dd>{g.misura}</dd>
          </dl>
        </article>
      ))}
    </div>
  );
}

function Promessa({ d }: { d: TimoneDto }) {
  return (
    <div className="tm-route">
      {(d.rotta.length ? d.rotta : []).slice(0, 3).map((g) => (
        <div key={g.id} className="tm-leg">
          <span className="tm-day">{g.titolo}</span>
          <span className="tm-gainline">Promesso: {h(g.benefici.ore_mese)} al mese</span>
          <p style={{ margin: 0, color: 'var(--tm-mute)', fontSize: 13.5 }}>
            Misurato: dal primo mese, con i compiti e le procedure svolte in Timonio, mai con stime.
          </p>
        </div>
      ))}
      {!d.rotta.length && (
        <p className="tm-grey">
          Ogni obiettivo accettato avrà il promesso accanto al misurato, mese per mese.
        </p>
      )}
    </div>
  );
}
